import type { Firm } from './model';

/// 一覧「次アクション」列の注意表示ロジック (Issue #524 PR4)。
///
/// 営業アクション由来の `要確認` / `更新` だけを扱う純粋関数。 メモ・契約・所属弁護士の
/// 更新は混ぜない (それらは PR5 の汎用タブ更新ドットの領分)。 件数・派生値は backend が
/// 集計済みで、 ここでは表示種別とラベルへ写すだけ (DOM / store / Svelte 非依存)。
/// 法律事務所一覧 (firm 投影) と弁護士一覧 (lawyer 投影 / 86eya0dva PR3 で新設) が
/// 同じ派生値形を持つため、 入力は構造型 `NextActionAttentionSource` で受ける。

/// 注意表示の導出に必要な派生値の形 (`Firm` / `LawyerBase` の共通部分)。
export type NextActionAttentionSource = Pick<
  Firm,
  | 'next_action_attention'
  | 'next_action_review_required'
  | 'next_action_review_reason'
  | 'current_action_review_required_count'
  | 'current_action_unseen_update_count'
>;

/// 注意表示の種別。
/// - `review`: 明示的要確認 (review_required) — `要確認` バッジ。 自動追加なら `自動` も併記。
/// - `update`: 他ユーザーによる未読更新のみ — `更新` バッジ。
/// - `none`:   注意表示なし。
export type NextActionAttentionKind = 'none' | 'review' | 'update';

export interface NextActionAttention {
  kind: NextActionAttentionKind;
  /// `auto_added` 由来の `自動` ラベルを併記するか (`kind === 'review'` のときのみ true)。
  showAuto: boolean;
}

/// 派生値から次アクション注意表示の種別を決める。
/// 要確認 (review_required) を更新 (unseen update) より優先する。
/// 要確認かつ更新の行があっても、 一覧では確認状態である `要確認` を代表表示にする。
export function nextActionAttention(
  source: NextActionAttentionSource,
): NextActionAttention {
  if (!source.next_action_attention) return { kind: 'none', showAuto: false };
  if (source.next_action_review_required) {
    return {
      kind: 'review',
      showAuto: source.next_action_review_reason === 'auto_added',
    };
  }
  return { kind: 'update', showAuto: false };
}

/// 色だけに依存しない読み上げ (a11y)。 要確認 / 更新の有無と件数を明示する。
/// `none` のとき (注意表示を描画しない) は空文字列。
export function nextActionAttentionAriaLabel(
  source: NextActionAttentionSource,
): string {
  const attention = nextActionAttention(source);
  if (attention.kind === 'review') {
    const count = source.current_action_review_required_count ?? 0;
    const auto = attention.showAuto ? '（自動追加あり）' : '';
    return `要確認 ${count}件${auto}`;
  }
  if (attention.kind === 'update') {
    const updates = source.current_action_unseen_update_count ?? 0;
    return `未確認の更新 ${updates}件`;
  }
  return '';
}
